/* guidescope-calculator.js — Ontario Telescope
   © 2026 Ontario Telescope and Accessories Inc. All rights reserved.
   DOM layer only. All arithmetic lives in guidescope-math.js; this file
   reads the form, calls solve(), and writes HTML. Keeping the split means
   the model stays unit-testable in Node.

   Loaded as a module from the section, which passes asset URLs on data-
   attributes because Shopify appends a ?v= cache-buster to asset_url.
*/

const root = document.querySelector('[data-guidescope-calculator]');
if (root) init(root).catch((err) => {
  console.error('[guidescope-calculator]', err);
  const n = root.querySelector('[data-gsc-boot]');
  if (n) n.textContent = 'The calculator could not load. Please refresh, or contact us and we will run the numbers for you.';
});

/* The section passes Shopify's asset_url on a data attribute, because asset_url
   appends a ?v= cache-buster we cannot guess. Fall back to resolving against
   this module's own URL if the attribute is missing or arrives unrendered —
   the sibling assets sit in the same directory either way. */
function assetUrl(attr, filename) {
  const v = (attr || '').trim();
  if (v && !v.includes('{{') && !v.includes('{%')) return v;
  return new URL(filename, import.meta.url).href;
}

async function init(el) {
  const M = await import(assetUrl(el.dataset.mathUrl, 'guidescope-math.js'));
  const D = (await import(assetUrl(el.dataset.dataUrl, 'guidescope-data.js'))).default;

  /* ---------------- helpers ---------------- */
  const $ = (id) => document.getElementById(id);
  const num = (id, dflt = null) => {
    const n = parseFloat(($(id) || {}).value);
    return Number.isFinite(n) ? n : dflt;
  };
  const val = (id) => (($(id) || {}).value || '');
  const esc = (s) => String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  const fmt = (x, d = 2) => (x == null || !Number.isFinite(x)) ? '—' : x.toFixed(d);
  const money = (x) => x == null ? '' : '$' + Number(x).toFixed(2) + ' CAD';
  const opt = (v, label) => { const o = document.createElement('option'); o.value = v; o.textContent = label; return o; };

  function groupInto(sel, rows, keyFn, labelFn, valueFn, firstOption) {
    sel.innerHTML = '';
    if (firstOption) sel.appendChild(opt(firstOption[0], firstOption[1]));
    const groups = new Map();
    rows.forEach((r) => {
      const k = keyFn(r);
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(r);
    });
    // "Generic / by sensor" is a fallback, not a brand — it belongs at the
    // bottom of the list rather than alphabetically among the manufacturers.
    const last = (k) => (String(k).startsWith('Generic') ? 1 : 0);
    [...groups.keys()]
      .sort((a, b) => last(a) - last(b) || String(a).localeCompare(String(b)))
      .forEach((k) => {
      const og = document.createElement('optgroup');
      og.label = k;
      groups.get(k).forEach((r) => og.appendChild(opt(valueFn(r), labelFn(r))));
      sel.appendChild(og);
    });
  }

  let mode = 'guidescope';

  /* ---------------- populate ---------------- */
  groupInto($('gsc-tel'), D.telescopes, (t) => t.make,
    (t) => `${t.model} — ${t.ap}mm f/${(t.fl / t.ap).toFixed(1)}`,
    (t) => t.id, ['custom', 'Custom / not listed']);

  groupInto($('gsc-cam'), D.cameras, (c) => c.make,
    (c) => `${c.model} (${c.sensor}, ${c.px}µm)`,
    (c) => c.id, ['custom', 'Custom / not listed']);

  const red = $('gsc-red');
  D.reducers.forEach((r) => red.appendChild(opt(r.id, r.label)));
  red.value = 'none';

  // Sourced from the model rather than hard-coded in the markup, so these
  // can never drift from the values solve() actually uses.
  Object.entries(M.DESIGNS).forEach(([k, d]) => $('gsc-design').appendChild(opt(k, d.label)));
  Object.entries(M.SKY_BRIGHTNESS).forEach(([k, v]) => $('gsc-vsky').appendChild(opt(String(v.v), v.label)));
  Object.values(M.SKY_REGIONS).forEach((r) => $('gsc-sky').appendChild(opt(String(r.mult), r.label)));
  Object.entries(M.GLASS).forEach(([k, g]) => $('gsc-gglass').appendChild(opt(k, g.label)));
  Object.entries(M.MOUNTS).forEach(([k, m]) => $('gsc-mount').appendChild(opt(k, m.label)));

  groupInto($('gsc-gscope'), D.guideScopes, (g) => g.v,
    (g) => `${g.t.replace(/\s+/g, ' ')} — ${g.fl}mm`, (g) => g.u,
    ['custom', 'Custom / not listed']);
  groupInto($('gsc-gcam'), D.guideCameras, (c) => c.make,
    (c) => `${c.model} — ${c.pxs}µm ${c.w}×${c.h} ${c.mono ? 'mono' : 'colour'}${c.u ? '' : ' ·'}`,
    (c) => c.id, ['custom', 'Custom / not listed']);
  groupInto($('gsc-oag'), D.oags, (o) => o.v,
    (o) => `${o.t} — ${o.ps}×${o.pl}mm prism`, (o) => o.u,
    ['custom', 'Custom / not listed']);

  const byUrl = (arr) => Object.fromEntries(arr.map((x) => [x.u, x]));
  const GS = byUrl(D.guideScopes), OA = byUrl(D.oags);
  const GC = Object.fromEntries(D.guideCameras.map((c) => [c.id, c]));

  /* ---------------- back-fill ---------------- */
  $('gsc-tel').addEventListener('change', function () {
    const t = D.telescopes.find((x) => x.id === this.value);
    if (!t) return;
    $('gsc-fl').value = t.fl; $('gsc-ap').value = t.ap;
    $('gsc-obs').value = (t.obs * 100).toFixed(0);
    $('gsc-design').value = M.designFromType(t.ty, t.ft, t.cc);
    $('gsc-tel-note').textContent =
      t.ft === 'optional' ? 'This model takes an optional flattener — switch the design to "Refractor + flattener" if you use one.'
      : t.cc === 'optional' ? 'Coma correctors are sold separately for this one — switch the design to "Newtonian + coma corrector" if you use one.'
      : '';
  });
  $('gsc-cam').addEventListener('change', function () {
    const c = D.cameras.find((x) => x.id === this.value);
    if (!c) return;
    $('gsc-px').value = c.px; $('gsc-resx').value = c.w; $('gsc-resy').value = c.h;
  });
  $('gsc-gscope').addEventListener('change', function () {
    const g = GS[this.value]; if (!g) return;
    $('gsc-gfl').value = g.fl; $('gsc-gap').value = g.ap;
    if (g.gl) $('gsc-gglass').value = g.gl;
    $('gsc-gap-note').textContent = g.apEst
      ? 'Aperture not published for this model — estimated from focal length at f/4.' : '';
  });
  $('gsc-gcam').addEventListener('change', function () {
    const c = GC[this.value]; if (!c) return;
    $('gsc-gpx').value = c.pxs; $('gsc-gresx').value = c.w; $('gsc-gresy').value = c.h;
    $('gsc-gmono').value = c.mono ? '1' : '0';
    // Quantum efficiency and read noise come from the verified camera list
    // rather than being guessed from the sensor name.
    $('gsc-qe').value = c.qe || 'modern_mono';
    if (c.rn != null) $('gsc-rn').value = c.rn;
  });
  $('gsc-oag').addEventListener('change', function () {
    const o = OA[this.value]; if (!o) return;
    $('gsc-prism-s').value = o.ps; $('gsc-prism-l').value = o.pl;
    if (o.th) $('gsc-oagth').value = o.th;
    $('gsc-oag-note').textContent = o.th ? ''
      : 'Optical length is not published for this model — check the manual and adjust.';
  });
  $('gsc-red').addEventListener('change', function () {
    const r = D.reducers.find((x) => x.id === this.value);
    $('gsc-red-custom-field').hidden = !(r && r.f == null);
  });

  /* ---------------- mode ---------------- */
  function setMode(m) {
    mode = m;
    [...root.querySelectorAll('.gsc-modeswitch button')].forEach((b) => {
      b.classList.toggle('gsc-on', b.dataset.mode === m);
      b.setAttribute('aria-pressed', b.dataset.mode === m ? 'true' : 'false');
    });
    $('gsc-panel-scope').hidden = m !== 'guidescope';
    $('gsc-panel-oag').hidden = m !== 'oag';
  }
  [...root.querySelectorAll('.gsc-modeswitch button')].forEach((b) => {
    b.addEventListener('click', () => { setMode(b.dataset.mode); calculate(); });
  });

  /* ---------------- read the form ---------------- */
  function readConfig() {
    const redFactor = (D.reducers.find((r) => r.id === val('gsc-red')) || {}).f;
    return {
      mode,
      imgFL: num('gsc-fl'), imgAperture: num('gsc-ap'),
      imgObstruction: (num('gsc-obs', 0) || 0) / 100,
      imgDesign: val('gsc-design') || 'refractor_flat',
      reducer: redFactor == null ? (num('gsc-red-custom', 1) || 1) : redFactor,
      imgPixel: num('gsc-px'), imgBin: num('gsc-bin', 1),
      imgResX: num('gsc-resx'), imgResY: num('gsc-resy'),
      guidePixel: num('gsc-gpx'), guideResX: num('gsc-gresx'), guideResY: num('gsc-gresy'),
      guideMono: val('gsc-gmono') === '1', guideBin: num('gsc-gbin', 1),
      guideQeProfile: val('gsc-qe') || 'modern_mono', guideReadNoise: num('gsc-rn', 2.5),
      guideFL: num('gsc-gfl'), guideAperture: num('gsc-gap'),
      guideGlass: val('gsc-gglass') || 'achromat',
      prismShort: num('gsc-prism-s'), prismLong: num('gsc-prism-l'),
      oagThickness: num('gsc-oagth'), filterWheelMm: num('gsc-fw', 0) || 0,
      cameraBackFocusMm: num('gsc-cambf', 17.5), spacersMm: num('gsc-spacer', 0) || 0,
      requiredBackFocus: num('gsc-reqbf', 55),
      prismEdge: val('gsc-prism-edge') || 'short', imageCircleMm: num('gsc-circle', 44),
      exposureS: num('gsc-exp', 2), seeing: num('gsc-seeing', 2.5),
      vSky: parseFloat(val('gsc-vsky')) || 19.9,
      skyMult: parseFloat(val('gsc-sky')) || M.SKY_REGIONS.mid.mult,
      flexureBaselineMm: num('gsc-baseline', 200),
      mountType: val('gsc-mount') || 'worm',
    };
  }

  /* ---------------- render pieces ---------------- */
  const kpi = (l, v, d, hero) =>
    `<div class="gsc-kpi"><div class="gsc-l">${l}</div>` +
    `<div class="gsc-v${hero ? ' gsc-hero' : ''}">${v}</div><div class="gsc-d">${d}</div></div>`;

  const alertBox = (t, title, body) => {
    const cls = t === 'ok' ? 'gsc-ok' : t === 'bad' ? 'gsc-bad' : '';
    const ic = t === 'ok' ? '✓' : t === 'bad' ? '✕' : '⚠';
    return `<div class="gsc-alert ${cls}"><div class="gsc-ic">${ic}</div><div><b>${title}</b><br>${body}</div></div>`;
  };
  const dlRow = (k, v) => `<div><dt>${k}</dt><dd>${v}</dd></div>`;
  const CLS = { excellent: 'ok', good: 'ok', marginal: 'warn', poor: 'bad' };

  function prodCard(p, why) {
    return `<a class="gsc-prodcard" href="${esc(p.u)}" target="_blank" rel="noopener">` +
      (p.img ? `<img loading="lazy" src="${esc(p.img)}" alt="${esc(p.t)}">` : '') +
      `<div><h4>${esc(p.t)}</h4><p>${esc(why.spec)}</p>` +
      `<p class="gsc-price">${money(p.px)} · ${p.st ? 'in stock' : 'available to order'}</p>` +
      (why.tag ? `<span class="gsc-why">${esc(why.tag)}</span>` : '') + `</div></a>`;
  }

  /* ---------------- recommendations ---------------- */
  const trial = (cfg, over) => M.solve({ ...cfg, ...over });

  function recommendGuideScopes(cfg, R) {
    return D.guideScopes.map((g) => {
      const T = trial(cfg, { guideFL: g.fl, guideAperture: g.ap, guideGlass: g.gl || 'achromat' });
      const dev = R.idealGuideFL ? Math.log(g.fl / R.idealGuideFL) : 0;
      const err = (dev < 0 ? -dev : dev * 0.35) + (g.st ? 0 : 0.15)
        + ({ excellent: 0, good: 0.25, marginal: 1.2, poor: 3 }[T.verdict.key]);
      return { g, T, err };
    }).filter((x) => x.T.verdict.key !== 'poor')
      .sort((a, b) => a.err - b.err).slice(0, 3)
      .map((x) => prodCard(x.g, {
        spec: `${x.g.fl}mm f/${(x.g.fl / x.g.ap).toFixed(1)} · star ${fmt(x.T.guideStarFwhmPx, 1)} px across · guiding error ${fmt(x.T.errArcsec)}″`,
        tag: x.T.verdict.title,
      }));
  }

  function recommendGuideCams(cfg, R) {
    return D.guideCameras.filter((c) => c.u).map((c) => {
      const T = trial(cfg, {
        guidePixel: c.pxs, guideResX: c.w, guideResY: c.h, guideMono: !!c.mono,
        guideQeProfile: c.qe || 'modern_mono', guideReadNoise: c.rn == null ? 2 : c.rn,
      });
      const pen = ({ excellent: 0, good: 0.3, marginal: 1.2, poor: 3 }[T.verdict.key])
        + (c.mono ? 0 : 0.45) + (c.st ? 0 : 0.15) + T.errArcsec * 0.5;
      return { c, T, pen };
    }).sort((a, b) => a.pen - b.pen).slice(0, 3)
      .map((x) => prodCard(x.c, {
        spec: `${x.c.pxs}µm ${x.c.mono ? 'mono' : 'colour'} · star ${fmt(x.T.guideStarFwhmPx, 1)} px across · reaches V${fmt(x.T.limitMag, 1)}`,
        tag: x.T.verdict.title,
      }));
  }

  function recommendOags(cfg, R) {
    if (!R.oag) return [];
    return D.oags.map((o) => {
      const T = trial(cfg, { prismShort: o.ps, prismLong: o.pl, oagThickness: o.th || cfg.oagThickness });
      const fits = T.oag && T.oag.backFocus.status !== 'over';
      const pen = (1 - (T.oag ? T.oag.captureFraction : 0)) * 3 + (fits ? 0 : 2.5) + (o.st ? 0 : 0.15);
      return { o, T, fits, pen };
    }).sort((a, b) => a.pen - b.pen).slice(0, 3)
      .map((x) => prodCard(x.o, {
        spec: `${x.o.ps}×${x.o.pl}mm prism${x.o.th ? ` · ${x.o.th}mm optical length` : ''} · catches ${((x.T.oag ? x.T.oag.captureFraction : 0) * 100).toFixed(0)}% of the beam at f/${fmt(R.fRatio, 1)}`,
        tag: x.fits ? (x.T.oag.captureFraction > 0.95 ? 'Fills the prism, fits your back focus' : 'Fits your back focus') : 'Check back focus — this one is tight',
      }));
  }

  function scopeTable(cfg, R) {
    if (mode !== 'guidescope') return '';
    const rows = D.guideScopes.map((g) => ({ g, T: trial(cfg, { guideFL: g.fl, guideAperture: g.ap, guideGlass: g.gl || 'achromat' }) }))
      .sort((a, b) => a.g.fl - b.g.fl);
    const cur = cfg.guideFL;
    return '<div class="gsc-tablewrap"><table class="gsc-t">' +
      '<tr><th>Guide scope</th><th>Focal length</th><th>Guide scale</th><th>Star width</th>' +
      '<th>Guiding error</th><th>Guide stars</th><th>Verdict</th><th></th></tr>' +
      rows.map((r) => {
        const hl = Math.abs(r.g.fl - cur) < 1 ? ' class="gsc-hl"' : '';
        return `<tr${hl}><td>${esc(r.g.t)}</td>` +
          `<td class="gsc-mono">${r.g.fl} mm</td>` +
          `<td class="gsc-mono">${fmt(r.T.guideScale)}″/px</td>` +
          `<td class="gsc-mono">${fmt(r.T.guideStarFwhmPx, 1)} px</td>` +
          `<td class="gsc-mono">${fmt(r.T.errArcsec)}″</td>` +
          `<td class="gsc-mono">${r.T.starCount >= 10 ? Math.round(r.T.starCount) : fmt(r.T.starCount, 1)}</td>` +
          `<td><span class="gsc-flag gsc-${CLS[r.T.verdict.key]}">${r.T.verdict.title.split('—')[0].trim()}</span></td>` +
          `<td><a href="${esc(r.g.u)}" target="_blank" rel="noopener">view</a></td></tr>`;
      }).join('') + '</table></div>';
  }

  /* ---------------- main ---------------- */
  function calculate() {
    const cfg = readConfig();
    const missing = [];
    if (!(cfg.imgFL > 0)) missing.push('imaging focal length');
    if (!(cfg.imgAperture > 0)) missing.push('imaging aperture');
    if (!(cfg.imgPixel > 0)) missing.push('imaging pixel size');
    if (!(cfg.guidePixel > 0)) missing.push('guide camera pixel size');
    if (mode === 'guidescope' && !(cfg.guideFL > 0)) missing.push('guide scope focal length');
    if (missing.length) {
      $('gsc-results').style.display = 'block';
      $('gsc-verdict').className = 'gsc-verdict gsc-warn';
      $('gsc-verdict').innerHTML = `<span class="gsc-vk">Missing a number</span>` +
        `<span class="gsc-vd">Fill in the ${missing.join(', ')} and the rest will follow.</span>`;
      ['gsc-kpis', 'gsc-detail', 'gsc-stars', 'gsc-geometry', 'gsc-warnings', 'gsc-reco', 'gsc-table']
        .forEach((id) => { if ($(id)) $(id).innerHTML = ''; });
      return;
    }

    const R = M.solve(cfg);

    /* verdict + what limits it */
    const lim = R.limitingFactor;
    $('gsc-verdict').className = 'gsc-verdict gsc-' + CLS[R.verdict.key];
    $('gsc-verdict').innerHTML =
      `<span class="gsc-vk">${R.verdict.title}</span><span class="gsc-vd">${R.verdict.desc}` +
      (lim ? ` <b>The limiting factor here is ${lim}.</b>` : '') + `</span>`;

    /* Star bloat makes the error concrete: an RMS pointing error of sigma
       convolves the star, so FWHM grows as sqrt(FWHM^2 + (2.355 sigma)^2). */
    const bloat = R.errOverFwhm != null
      ? (Math.sqrt(1 + Math.pow(2.3548 * R.errOverFwhm, 2)) - 1) * 100 : null;

    $('gsc-kpis').innerHTML = [
      kpi('Guiding error', fmt(R.errArcsec) + '″',
        `${fmt(bloat, 1)}% wider stars &mdash; ${fmt(100 * R.errOverFwhm, 0)}% of the star's own width`, true),
      kpi('Guide star width', fmt(R.guideStarFwhm, 1) + '″',
        `${fmt(R.guideStarFwhmPx, 2)} guide pixels across${R.guideBin > 1 ? `, binned ${R.guideBin}×${R.guideBin}` : ''}`),
      kpi('Star you will guide on', 'V' + fmt(R.typicalStarMag, 1),
        `signal-to-noise ${fmt(R.typicalSnr, 0)} &mdash; ${R.regime}-limited`),
      kpi('Imaging scale', fmt(R.imageScale) + '″<span style="font-size:.6em">/px</span>',
        `${Math.round(R.effectiveFL)}mm at f/${fmt(R.fRatio, 1)}${cfg.imgBin > 1 ? `, bin ${cfg.imgBin}` : ''}`),
    ].join('');

    /* component scorecard */
    const NAMES = { precision: 'Measurement precision', sampling: 'Guide star sampling', 'guide stars': 'Guide star availability', signal: 'Guide star signal' };
    const WHY = {
      precision: `${fmt(R.errArcsec)}″ of measurement error against a ${fmt(R.imagingStarFwhm, 1)}″ star`,
      sampling: `${fmt(R.guideStarFwhmPx, 2)} pixels across the star`,
      'guide stars': `${R.starCount >= 10 ? Math.round(R.starCount) : fmt(R.starCount, 1)} expected in the guide field`,
      signal: `signal-to-noise ${fmt(R.typicalSnr, 0)} on that star`,
    };
    $('gsc-scorecard').innerHTML = '<dl class="gsc-dl">' + Object.entries(R.components).map(([k, v]) =>
      dlRow(`${NAMES[k]} <span class="gsc-sub" style="display:block">${WHY[k]}</span>`,
        `<span class="gsc-flag gsc-${CLS[v]}">${v}</span>`)).join('') + '</dl>';

    /* detail */
    const psf = R.psf;
    $('gsc-detail').innerHTML =
      '<div class="gsc-two"><div><h3 style="margin-top:0">Where the star width comes from</h3><dl class="gsc-dl">' +
      dlRow('Seeing', fmt(psf.seeing, 2) + '″') +
      dlRow('Diffraction at this aperture', fmt(psf.diffraction, 2) + '″') +
      (psf.optics > 0 ? dlRow('Guide scope glass (' + (M.GLASS[cfg.guideGlass] || M.GLASS.achromat).label.toLowerCase() + ')', fmt(psf.optics, 2) + '″') : '') +
      (psf.coma > 0 ? dlRow('Off-axis coma', fmt(psf.coma, 2) + '″') : '') +
      (psf.astig > 0 ? dlRow('Off-axis astigmatism', fmt(psf.astig, 2) + '″') : '') +
      dlRow('<b>Combined star width</b>', '<b>' + fmt(psf.fwhm, 2) + '″</b>') +
      '</dl></div><div><h3 style="margin-top:0">Where the error comes from</h3><dl class="gsc-dl">' +
      dlRow('Photon and background noise', fmt(R.centroid.random, 3) + '″') +
      dlRow('Pixel-phase sampling error', fmt(R.centroid.systematic, 3) + '″') +
      dlRow('<b>Total</b>', '<b>' + fmt(R.errArcsec, 3) + '″</b>') +
      dlRow('Limited by', R.centroid.limitedBy === 'sampling' ? 'sampling' : 'signal') +
      dlRow('Faintest star this reaches', 'V' + fmt(R.limitMag, 1)) +
      '</dl></div></div>' +
      '<dl class="gsc-dl" style="margin-top:1.1rem">' +
      dlRow('Guide scale', fmt(R.guideScale) + '″/px' + (R.guideBin > 1 ? ` (binned ${R.guideBin}×${R.guideBin})` : '')) +
      dlRow('Guide field of view', `${fmt(R.guideFovW, 0)}′ × ${fmt(R.guideFovH, 0)}′`) +
      dlRow('Imaging field of view', `${fmt(R.imgFovW, 0)}′ × ${fmt(R.imgFovH, 0)}′`) +
      dlRow('Guide-to-imaging scale ratio', fmt(R.ratio, 1) + '× <span class="gsc-sub">(shown for reference — it is not what decides the verdict)</span>') +
      (mode === 'guidescope' && R.minGuideFL ? dlRow('Shortest guide scope that works', Math.ceil(R.minGuideFL) + ' mm') : '') +
      (mode === 'guidescope' && R.idealGuideFL ? dlRow('Comfortable guide focal length', Math.ceil(R.idealGuideFL) + ' mm') : '') +
      '</dl>';

    /* guide stars */
    const sv = R.starVerdict;
    $('gsc-stars').innerHTML = '<div class="gsc-kpis">' +
      kpi('Faintest usable star', 'V' + fmt(R.limitMag, 1), `${cfg.exposureS}s, ${cfg.guideMono ? 'mono' : 'colour'}, ${R.regime}-limited`) +
      kpi('Guide field', `${fmt(R.guideFovW, 0)}′ × ${fmt(R.guideFovH, 0)}′`, mode === 'oag' ? 'limited by the prism' : 'full guide sensor') +
      kpi('Stars in field', R.starCount >= 10 ? Math.round(R.starCount) : fmt(R.starCount, 1), 'at this sky brightness and galactic latitude') +
      kpi('Availability', `<span class="gsc-flag gsc-${sv.cls}" style="font-size:.55em">${sv.label}</span>`, sv.desc) +
      '</div>';

    /* geometry */
    let geo = '';
    if (mode === 'oag' && R.oag) {
      const o = R.oag, bf = o.backFocus;
      geo = '<div class="gsc-two">' +
        '<div><h3 style="margin-top:0">Prism &amp; light cone</h3><dl class="gsc-dl">' +
        dlRow('Prism to focal plane', fmt(o.prismToFocus, 1) + ' mm') +
        dlRow('Light cone at the prism', fmt(o.beam, 2) + ' mm across') +
        dlRow('Prism face', `${cfg.prismShort} × ${cfg.prismLong} mm`) +
        dlRow('Beam caught by the prism', (o.captureFraction * 100).toFixed(0) + '%') +
        dlRow('Telescope illumination there', (o.illumination * 100).toFixed(0) + '%') +
        dlRow('<b>Light reaching the guide camera</b>', '<b>' + (o.totalThroughput * 100).toFixed(0) + '%</b>') +
        '</dl></div>' +
        '<div><h3 style="margin-top:0">Prism placement</h3><dl class="gsc-dl">' +
        dlRow('Sensor half-dimension to clear', fmt(o.placement.clearance, 1) + ' mm') +
        dlRow('Prism centre sits at', fmt(o.placement.centre, 1) + ' mm from axis') +
        dlRow('Guide star actually forms at', fmt(o.starRadius, 1) + ' mm from axis') +
        dlRow('Guide star is off-axis by', fmt(o.placement.offAxisArcmin, 0) + '′') +
        dlRow('Guide star needs a circle of', fmt(o.placement.guideStarCircle, 0) + ' mm') +
        dlRow('Your corrected field', fmt(cfg.imageCircleMm, 0) + ' mm') +
        '</dl></div></div>' +
        '<h3>Back-focus ledger</h3><dl class="gsc-dl">' +
        dlRow('Required by your flattener / reducer', fmt(cfg.requiredBackFocus, 1) + ' mm') +
        dlRow('Off-axis guider body', fmt(cfg.oagThickness, 1) + ' mm') +
        dlRow('Filter wheel', fmt(cfg.filterWheelMm, 1) + ' mm') +
        dlRow('Camera back focus', fmt(cfg.cameraBackFocusMm, 1) + ' mm') +
        dlRow('Spacers', fmt(cfg.spacersMm, 1) + ' mm') +
        dlRow('<b>Total used</b>', '<b>' + fmt(bf.used, 1) + ' mm</b>') +
        dlRow('<b>' + (bf.remaining >= 0 ? 'Spacer still to add' : 'Over budget by') + '</b>',
          `<b style="color:var(--gsc-${bf.status === 'over' ? 'error' : 'ok'})">${fmt(Math.abs(bf.remaining), 1)} mm</b>`) +
        dlRow('<b>Set the guide camera in by</b>', '<b>' + fmt(o.focusOffset, 2) + ' mm</b> <span class="gsc-sub">relative to the imaging sensor</span>') +
        '</dl>';
    } else if (R.flexure) {
      const f = R.flexure;
      geo = '<dl class="gsc-dl">' +
        dlRow('Acceptable drift over a sub', fmt(f.tolArcsec, 2) + '″ (half an imaging pixel)') +
        dlRow('Assumed mounting baseline', f.baselineMm + ' mm') +
        dlRow('<b>Maximum relative movement</b>', '<b>' + fmt(f.shiftUm, 2) + ' µm</b>') +
        '</dl>' +
        `<p class="gsc-sub" style="margin-top:.8rem;max-width:70ch">That is how far the guide scope may shift relative to the imaging tube across one exposure before stars start to trail. ${f.shiftUm < 1 ? 'Below a micron, which is beyond what rings and a dovetail hold reliably — this focal length really wants an off-axis guider.' : f.shiftUm < 3 ? 'Tight. Use rigid rings rather than an adjustable bracket, and bolt everything hard.' : 'Comfortably within what a solid guide scope mount will hold.'} Note that this is an angular tolerance: unlike pixel scale, it does not get easier at shorter imaging focal lengths.</p>`;
    }
    $('gsc-geometry').innerHTML = geo;
    $('gsc-geo-head').textContent = mode === 'oag' ? 'Off-axis geometry' : 'Differential flexure budget';
    $('gsc-geo-note').textContent = mode === 'oag'
      ? 'Where the prism sits, how much light survives to the guide camera, and whether the stack fits your back-focus budget.'
      : 'The failure mode a guide scope has and an off-axis guider does not: the two tubes moving relative to each other.';

    $('gsc-warnings').innerHTML = R.warnings.map((w) => alertBox(w.t, w.title, w.body)).join('');

    /* recommendations */
    const recos = [];
    if (mode === 'guidescope') {
      const s = recommendGuideScopes(cfg, R);
      if (s.length) recos.push('<h3>Guide scopes that suit this imaging train</h3><div class="gsc-prodcards">' + s.join('') + '</div>');
    } else {
      const o = recommendOags(cfg, R);
      if (o.length) recos.push('<h3>Off-axis guiders that suit this imaging train</h3><div class="gsc-prodcards">' + o.join('') + '</div>');
    }
    const c = recommendGuideCams(cfg, R);
    if (c.length) recos.push('<h3>Guide cameras that suit this imaging train</h3><div class="gsc-prodcards">' + c.join('') + '</div>');
    recos.push('<div class="gsc-shopcards">' +
      `<div class="gsc-shopcard"><h4>All guide scopes</h4><p>Starfield, ZWO, Askar, William Optics and Sky-Watcher, from 100&nbsp;mm mini scopes to 360&nbsp;mm ED doublets.</p><a href="${D.collections.guideScopes}">Shop guide scopes</a></div>` +
      `<div class="gsc-shopcard"><h4>All guide cameras</h4><p>Mono and colour guiders from ZWO, QHYCCD, ToupTek and Starlight Xpress.</p><a href="${D.collections.guideCameras}">Shop guide cameras</a></div>` +
      `<div class="gsc-shopcard"><h4>All off-axis guiders</h4><p>ZWO, QHYCCD, Celestron, Askar and Pegasus, in standard and extra-thin bodies.</p><a href="${D.collections.oags}">Shop off-axis guiders</a></div>` +
      '</div>');
    $('gsc-reco').innerHTML = recos.join('');

    /* PHD2 starting points */
    const P = M.phd2Settings(R, { mountType: cfg.mountType, exposureS: cfg.exposureS });
    $('gsc-phd2').innerHTML =
      '<div class="gsc-two"><div><h3 style="margin-top:0">Camera &amp; calibration</h3><dl class="gsc-dl">' +
      dlRow('Guide exposure', `${P.exposure.lo}&ndash;${P.exposure.hi} s`) +
      dlRow('Binning', `${P.binning} × ${P.binning}`) +
      dlRow('Calibration step size', P.calibrationStepMs + ' ms') +
      dlRow('Calibrate near', 'declination 0°, close to the meridian') +
      '</dl></div><div><h3 style="margin-top:0">Guide algorithms</h3><dl class="gsc-dl">' +
      dlRow('RA algorithm', P.raAlgorithm) +
      dlRow('Dec algorithm', P.decAlgorithm) +
      dlRow('Minimum move — start at', fmt(P.minMoveSuggestPx, 2) + ' px') +
      dlRow('Minimum move — never below', fmt(P.minMoveFloorPx, 3) + ' px') +
      '</dl></div></div>' +
      `<div class="gsc-callout"><h4>Why those two minimum-move numbers differ</h4>` +
      `<p>The floor is your measurement noise: ${fmt(R.errArcsec)}&Prime; converted into guide pixels. A correction smaller than that is chasing the guider's own uncertainty rather than the mount, and can never help.</p>` +
      `<p>The starting value adds the seeing. Atmospheric wobble that survives a ${fmt((P.exposure.lo + P.exposure.hi) / 2, 1)}&nbsp;s exposure is worth about ${fmt(P.seeingResidualArcsec)}&Prime; here, and it is gone by the time any correction lands — so chasing it just adds noise to your tracking. Note the trade-off this exposes: shorter exposures let you correct the mount faster but average away less seeing, which is why a fast-correcting mount wants a <i>larger</i> minimum move, not a smaller one.</p>` +
      `<p class="gsc-sub" style="margin-top:.6rem">Run the Guiding Assistant once your gear is on the sky &mdash; it measures your actual seeing rather than assuming it. Use these as the sanity check on what it reports.</p></div>` +
      `<p class="gsc-sub">${P.mount.note}</p>` +
      P.notes.map((n) => alertBox(n.t, n.title, n.body)).join('');

    $('gsc-table-wrap').hidden = mode !== 'guidescope';
    $('gsc-table').innerHTML = scopeTable(cfg, R);
    $('gsc-results').style.display = 'block';
  }

  /* ---------------- wiring ---------------- */
  $('gsc-calc').addEventListener('click', () => {
    calculate();
    $('gsc-results').scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
  $('gsc-reset').addEventListener('click', () => { applyDefaults(); calculate(); });
  const live = (e) => {
    if (e.target.closest('.gsc-group') && $('gsc-results').style.display === 'block') calculate();
  };
  root.addEventListener('input', live);
  root.addEventListener('change', live);

  function applyDefaults() {
    $('gsc-tel').value = 'custom';
    $('gsc-fl').value = 480; $('gsc-ap').value = 80; $('gsc-obs').value = 0;
    $('gsc-design').value = 'refractor_flat';
    $('gsc-red').value = 'none'; $('gsc-red-custom-field').hidden = true;
    $('gsc-cam').value = 'custom';
    $('gsc-px').value = 3.76; $('gsc-resx').value = 6248; $('gsc-resy').value = 4176; $('gsc-bin').value = 1;
    $('gsc-gscope').value = 'custom'; $('gsc-gfl').value = 240; $('gsc-gap').value = 60;
    $('gsc-gglass').value = 'achromat';
    $('gsc-gcam').value = 'custom';
    $('gsc-gpx').value = 3.75; $('gsc-gresx').value = 1280; $('gsc-gresy').value = 960;
    $('gsc-gmono').value = '1'; $('gsc-gbin').value = '1';
    $('gsc-qe').value = 'modern_mono'; $('gsc-rn').value = 2;
    $('gsc-oag').value = 'custom';
    $('gsc-prism-s').value = 8; $('gsc-prism-l').value = 8; $('gsc-oagth').value = 16.5;
    $('gsc-fw').value = 0; $('gsc-cambf').value = 17.5; $('gsc-spacer').value = 0; $('gsc-reqbf').value = 55;
    $('gsc-prism-edge').value = 'short'; $('gsc-circle').value = 44;
    $('gsc-exp').value = 2; $('gsc-seeing').value = 2.5;
    $('gsc-vsky').value = String(M.SKY_BRIGHTNESS.b5.v);
    $('gsc-sky').value = String(M.SKY_REGIONS.mid.mult);
    $('gsc-baseline').value = 200;
    $('gsc-mount').value = 'worm';
    $('gsc-gap-note').textContent = ''; $('gsc-oag-note').textContent = '';
    $('gsc-tel-note').textContent = '';
  }

  applyDefaults();
  setMode('guidescope');
  el.querySelector('[data-gsc-app]').hidden = false;
  const boot = el.querySelector('[data-gsc-boot]');
  if (boot) boot.remove();
}
